/**
 * Program Service
 * ===============
 * Handles program (playlist) management operations.
 */

import { supabase } from '../lib/supabase'
import type {
  Program,
  ProgramInsert,
  ProgramUpdate,
  ProgramItem,
  ProgramItemInsert,
  ProgramItemUpdate,
  ProgramLikeInsert,
  ProgramSaveInsert,
  Video,
  VideoVisibility,
  DifficultyLevel,
} from '../types/database.types'

export interface ProgramItemWithVideo extends ProgramItem {
  video: Pick<Video, 'id' | 'title' | 'thumbnail_url' | 'duration_seconds' | 'mux_playback_id' | 'status' | 'thumbnail_time'>
}

export interface ProgramWithItems extends Program {
  items: ProgramItemWithVideo[]
  item_count?: number
  total_duration?: number
}

export interface ProgramFilters {
  visibility?: VideoVisibility
  search?: string
  isTemplate?: boolean
}

export interface PublicProgramAuthor {
  id: string
  full_name: string
  avatar_url?: string | null
  specialty?: string | null
}

export interface PublicProgramWithAuthor extends Program {
  author: PublicProgramAuthor
  item_count?: number
  is_liked?: boolean
  is_saved?: boolean
}

export interface PublicProgramDetailWithItems extends PublicProgramWithAuthor {
  items: ProgramItemWithVideo[]
  total_duration?: number
}

export interface PublicProgramFilters {
  search?: string
  category?: string
  difficulty?: string
  minDuration?: number
  maxDuration?: number
  sortBy?: 'newest' | 'popular' | 'most_duplicated'
}

export const programService = {
  /**
   * Get all programs for a user
   */
  async getPrograms(
    userId: string,
    filters?: ProgramFilters
  ): Promise<Program[]> {
    let query = supabase
      .from('programs')
      .select('*')
      .eq('user_id', userId)
      .order('updated_at', { ascending: false })

    if (filters?.visibility) {
      query = query.eq('visibility', filters.visibility)
    }

    if (filters?.isTemplate !== undefined) {
      query = query.eq('is_template', filters.isTemplate)
    }

    if (filters?.search) {
      query = query.ilike('title', `%${filters.search}%`)
    }

    const { data, error } = await query

    if (error) {
      console.error('Failed to fetch programs:', error)
      throw error
    }

    return data || []
  },

  /**
   * Get a single program by ID with its items
   */
  async getProgramById(programId: string): Promise<ProgramWithItems | null> {
    const { data: program, error: programError } = await supabase
      .from('programs')
      .select('*')
      .eq('id', programId)
      .single()

    if (programError) {
      if (programError.code === 'PGRST116') return null
      console.error('Failed to fetch program:', programError)
      throw programError
    }

    // Fetch program items with video details
    const { data: items, error: itemsError } = await supabase
      .from('program_items')
      .select(
        `
        *,
        video:videos (
          id,
          title,
          thumbnail_url,
          duration_seconds,
          mux_playback_id,
          status,
          thumbnail_time
        )
      `
      )
      .eq('program_id', programId)
      .order('position', { ascending: true })

    if (itemsError) {
      console.error('Failed to fetch program items:', itemsError)
      throw itemsError
    }

    const programItems = (items || []).map((item) => ({
      ...item,
      video: item.video as Pick<
        Video,
        'id' | 'title' | 'thumbnail_url' | 'duration_seconds' | 'mux_playback_id' | 'status' | 'thumbnail_time'
      >,
    }))

    return {
      ...program,
      items: programItems,
      item_count: programItems.length,
      total_duration: programItems.reduce(
        (sum, item) => sum + (item.video?.duration_seconds || 0),
        0
      ),
    }
  },

  /**
   * Create a new program
   */
  async createProgram(program: ProgramInsert): Promise<Program> {
    const { data, error } = await supabase
      .from('programs')
      .insert(program)
      .select()
      .single()

    if (error) {
      console.error('Failed to create program:', error)
      throw error
    }

    return data
  },

  /**
   * Update an existing program
   */
  async updateProgram(
    programId: string,
    updates: ProgramUpdate
  ): Promise<Program | null> {
    const { data, error } = await supabase
      .from('programs')
      .update(updates)
      .eq('id', programId)
      .select()
      .single()

    if (error) {
      if (error.code === 'PGRST116') return null
      console.error('Failed to update program:', error)
      throw error
    }

    return data
  },

  /**
   * Delete a program
   */
  async deleteProgram(programId: string): Promise<boolean> {
    const { error } = await supabase
      .from('programs')
      .delete()
      .eq('id', programId)

    if (error) {
      console.error('Failed to delete program:', error)
      throw error
    }

    return true
  },

  /**
   * Add a video to a program
   */
  async addVideoToProgram(
    programId: string,
    videoId: string,
    options?: Partial<ProgramItemInsert>
  ): Promise<ProgramItem> {
    // Get the max position for this program
    const { data: existingItems } = await supabase
      .from('program_items')
      .select('position')
      .eq('program_id', programId)
      .order('position', { ascending: false })
      .limit(1)

    const nextPosition = existingItems?.[0]?.position ?? -1
    const newPosition = nextPosition + 1

    const { data, error } = await supabase
      .from('program_items')
      .insert({
        program_id: programId,
        video_id: videoId,
        position: newPosition,
        ...options,
      })
      .select()
      .single()

    if (error) {
      console.error('Failed to add video to program:', error)
      throw error
    }

    return data
  },

  /**
   * Remove a video from a program
   */
  async removeVideoFromProgram(programItemId: string): Promise<boolean> {
    const { error } = await supabase
      .from('program_items')
      .delete()
      .eq('id', programItemId)

    if (error) {
      console.error('Failed to remove video from program:', error)
      throw error
    }

    return true
  },

  /**
   * Update a program item
   */
  async updateProgramItem(
    programItemId: string,
    updates: ProgramItemUpdate
  ): Promise<ProgramItem | null> {
    const { data, error } = await supabase
      .from('program_items')
      .update(updates)
      .eq('id', programItemId)
      .select()
      .single()

    if (error) {
      if (error.code === 'PGRST116') return null
      console.error('Failed to update program item:', error)
      throw error
    }

    return data
  },

  /**
   * Reorder program items
   */
  async reorderProgramItems(
    programId: string,
    itemIds: string[]
  ): Promise<boolean> {
    // Update positions for all items
    const updates = itemIds.map((id, index) =>
      supabase
        .from('program_items')
        .update({ position: index })
        .eq('id', id)
        .eq('program_id', programId)
    )

    const results = await Promise.all(updates)
    const hasError = results.some((r) => r.error)

    if (hasError) {
      console.error('Failed to reorder program items')
      throw new Error('Failed to reorder program items')
    }

    return true
  },

  /**
   * Duplicate a program
   */
  async duplicateProgram(
    programId: string,
    userId: string,
    newTitle?: string
  ): Promise<ProgramWithItems | null> {
    // Get the original program with items
    const original = await this.getProgramById(programId)
    if (!original) return null

    // Create the new program
    const newProgram = await this.createProgram({
      user_id: userId,
      title: newTitle || `${original.title} (Copy)`,
      description: original.description,
      visibility: 'private', // Always start as private
      is_template: false,
      duration_weeks: original.duration_weeks,
      difficulty_level: original.difficulty_level,
    })

    // Copy all items
    if (original.items.length > 0) {
      const newItems = original.items.map((item, index) => ({
        program_id: newProgram.id,
        video_id: item.video_id,
        position: index,
        sets: item.sets,
        reps: item.reps,
        duration_seconds: item.duration_seconds,
        rest_seconds: item.rest_seconds,
        day_of_week: item.day_of_week,
        notes: item.notes,
      }))

      const { error } = await supabase.from('program_items').insert(newItems)

      if (error) {
        console.error('Failed to copy program items:', error)
        // Clean up the program if items failed
        await this.deleteProgram(newProgram.id)
        throw error
      }
    }

    // Return the complete new program
    return this.getProgramById(newProgram.id)
  },

  /**
   * Get public/template programs
   */
  async getPublicPrograms(limit = 20): Promise<Program[]> {
    const { data, error } = await supabase
      .from('programs')
      .select('*')
      .eq('visibility', 'public')
      .eq('is_template', true)
      .order('created_at', { ascending: false })
      .limit(limit)

    if (error) {
      console.error('Failed to fetch public programs:', error)
      throw error
    }

    return data || []
  },

  /**
   * Get program count for a user
   */
  async getProgramCount(userId: string): Promise<number> {
    const { count, error } = await supabase
      .from('programs')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', userId)

    if (error) {
      console.error('Failed to get program count:', error)
      return 0
    }

    return count || 0
  },

  /**
   * Toggle template status for a program
   */
  async toggleTemplate(programId: string, isTemplate: boolean): Promise<Program | null> {
    const { data, error } = await supabase
      .from('programs')
      .update({ is_template: isTemplate })
      .eq('id', programId)
      .select()
      .single()

    if (error) {
      console.error('Failed to toggle template:', error)
      throw error
    }

    return data
  },

  /**
   * Get user's template programs
   */
  async getTemplates(userId: string): Promise<Program[]> {
    const { data, error } = await supabase
      .from('programs')
      .select('*')
      .eq('user_id', userId)
      .eq('is_template', true)
      .order('title', { ascending: true })

    if (error) {
      console.error('Failed to fetch templates:', error)
      throw error
    }

    return data || []
  },

  /**
   * Duplicate a program item (task) to another program
   */
  async duplicateTaskToProgram(
    sourceItemId: string,
    targetProgramId: string
  ): Promise<ProgramItem> {
    // Get the source item
    const { data: sourceItem, error: fetchError } = await supabase
      .from('program_items')
      .select('*')
      .eq('id', sourceItemId)
      .single()

    if (fetchError || !sourceItem) {
      throw new Error('Source task not found')
    }

    // Get the max position in target program
    const { data: existingItems } = await supabase
      .from('program_items')
      .select('position')
      .eq('program_id', targetProgramId)
      .order('position', { ascending: false })
      .limit(1)

    const nextPosition = (existingItems?.[0]?.position ?? -1) + 1

    // Create the new item
    const { data, error } = await supabase
      .from('program_items')
      .insert({
        program_id: targetProgramId,
        video_id: sourceItem.video_id,
        position: nextPosition,
        sets: sourceItem.sets,
        reps: sourceItem.reps,
        duration_seconds: sourceItem.duration_seconds,
        rest_seconds: sourceItem.rest_seconds,
        day_of_week: sourceItem.day_of_week,
        notes: sourceItem.notes,
      })
      .select()
      .single()

    if (error) {
      console.error('Failed to duplicate task:', error)
      throw error
    }

    return data
  },

  // ============================================
  // PUBLIC PROGRAMS - Browse, Like, Save, Duplicate
  // ============================================

  /**
   * Browse public programs with author info and filters
   */
  async browsePublicPrograms(
    filters?: PublicProgramFilters,
    currentUserId?: string,
    limit = 20,
    offset = 0
  ): Promise<{ programs: PublicProgramWithAuthor[]; total: number }> {
    // Build the query for public programs
    let query = supabase
      .from('programs')
      .select('*', { count: 'exact' })
      .eq('visibility', 'public')

    // Apply filters
    if (filters?.search) {
      query = query.or(
        `title.ilike.%${filters.search}%,description.ilike.%${filters.search}%`
      )
    }

    if (filters?.category) {
      query = query.eq('category', filters.category)
    }

    if (filters?.difficulty) {
      query = query.eq('difficulty_level', filters.difficulty as DifficultyLevel)
    }

    if (filters?.minDuration) {
      query = query.gte('duration_weeks', filters.minDuration)
    }

    if (filters?.maxDuration) {
      query = query.lte('duration_weeks', filters.maxDuration)
    }

    // Apply sorting
    switch (filters?.sortBy) {
      case 'popular':
        query = query.order('like_count', { ascending: false })
        break
      case 'most_duplicated':
        query = query.order('duplicate_count', { ascending: false })
        break
      case 'newest':
      default:
        query = query.order('created_at', { ascending: false })
    }

    // Apply pagination
    query = query.range(offset, offset + limit - 1)

    const { data, error, count } = await query

    if (error) {
      console.error('Failed to browse public programs:', error)
      throw error
    }

    if (!data?.length) {
      return { programs: [], total: 0 }
    }

    // Get unique author IDs and fetch user profiles
    const authorIds = [...new Set(data.map((p) => p.user_id))]
    const { data: authors } = await supabase
      .from('users')
      .select('id, full_name, avatar_url, specialty')
      .in('id', authorIds)

    const authorMap = new Map<string, PublicProgramAuthor>()
    authors?.forEach((a) => {
      authorMap.set(a.id, {
        id: a.id,
        full_name: a.full_name || 'Community Coach',
        avatar_url: a.avatar_url,
        specialty: a.specialty,
      })
    })

    // Get item counts for all programs
    const programIds = data.map((p) => p.id)
    const { data: itemCounts } = await supabase
      .from('program_items')
      .select('program_id')
      .in('program_id', programIds)

    const countMap = new Map<string, number>()
    itemCounts?.forEach((item) => {
      countMap.set(item.program_id, (countMap.get(item.program_id) || 0) + 1)
    })

    // If user is logged in, check which programs they've liked/saved
    let likedIds = new Set<string>()
    let savedIds = new Set<string>()

    if (currentUserId) {
      const [likesResult, savesResult] = await Promise.all([
        supabase
          .from('program_likes')
          .select('program_id')
          .eq('user_id', currentUserId)
          .in('program_id', programIds),
        supabase
          .from('program_saves')
          .select('program_id')
          .eq('user_id', currentUserId)
          .in('program_id', programIds),
      ])

      if (likesResult.data) {
        likedIds = new Set(likesResult.data.map((l) => l.program_id))
      }
      if (savesResult.data) {
        savedIds = new Set(savesResult.data.map((s) => s.program_id))
      }
    }

    const programs: PublicProgramWithAuthor[] = data.map((p) => ({
      ...p,
      author: authorMap.get(p.user_id) || {
        id: p.user_id,
        full_name: 'Community Coach',
        avatar_url: null,
        specialty: null,
      },
      item_count: countMap.get(p.id) || 0,
      is_liked: likedIds.has(p.id),
      is_saved: savedIds.has(p.id),
    }))

    return {
      programs,
      total: count || 0,
    }
  },

  /**
   * Get a single public program with author info
   */
  async getPublicProgramById(
    programId: string,
    currentUserId?: string
  ): Promise<PublicProgramWithAuthor | null> {
    const { data, error } = await supabase
      .from('programs')
      .select('*')
      .eq('id', programId)
      .eq('visibility', 'public')
      .single()

    if (error) {
      if (error.code === 'PGRST116') return null
      console.error('Failed to fetch public program:', error)
      throw error
    }

    // Fetch author info
    const { data: author } = await supabase
      .from('users')
      .select('id, full_name, avatar_url, specialty')
      .eq('id', data.user_id)
      .single()

    // Get item count
    const { count: itemCount } = await supabase
      .from('program_items')
      .select('*', { count: 'exact', head: true })
      .eq('program_id', programId)

    let isLiked = false
    let isSaved = false

    if (currentUserId) {
      const [likeResult, saveResult] = await Promise.all([
        supabase
          .from('program_likes')
          .select('id')
          .eq('program_id', programId)
          .eq('user_id', currentUserId)
          .maybeSingle(),
        supabase
          .from('program_saves')
          .select('id')
          .eq('program_id', programId)
          .eq('user_id', currentUserId)
          .maybeSingle(),
      ])

      isLiked = !!likeResult.data
      isSaved = !!saveResult.data
    }

    return {
      ...data,
      author: author ? {
        id: author.id,
        full_name: author.full_name || 'Community Coach',
        avatar_url: author.avatar_url,
        specialty: author.specialty,
      } : {
        id: data.user_id,
        full_name: 'Community Coach',
        avatar_url: null,
        specialty: null,
      },
      item_count: itemCount || 0,
      is_liked: isLiked,
      is_saved: isSaved,
    }
  },

  /**
   * Get a public program with full items/exercises for detail page
   */
  async getPublicProgramWithItems(
    programId: string,
    currentUserId?: string
  ): Promise<PublicProgramDetailWithItems | null> {
    // Fetch program
    const { data, error } = await supabase
      .from('programs')
      .select('*')
      .eq('id', programId)
      .eq('visibility', 'public')
      .single()

    if (error) {
      if (error.code === 'PGRST116') return null
      console.error('Failed to fetch public program:', error)
      throw error
    }

    // Fetch author info
    const { data: author } = await supabase
      .from('users')
      .select('id, full_name, avatar_url, specialty')
      .eq('id', data.user_id)
      .single()

    // Fetch program items with video details
    const { data: items, error: itemsError } = await supabase
      .from('program_items')
      .select(
        `
        *,
        video:videos (
          id,
          title,
          thumbnail_url,
          duration_seconds,
          mux_playback_id,
          status,
          thumbnail_time
        )
      `
      )
      .eq('program_id', programId)
      .order('position', { ascending: true })

    if (itemsError) {
      console.error('Failed to fetch program items:', itemsError)
      throw itemsError
    }

    const programItems = (items || []).map((item) => ({
      ...item,
      video: item.video as Pick<
        Video,
        'id' | 'title' | 'thumbnail_url' | 'duration_seconds' | 'mux_playback_id' | 'status' | 'thumbnail_time'
      >,
    }))

    let isLiked = false
    let isSaved = false

    if (currentUserId) {
      const [likeResult, saveResult] = await Promise.all([
        supabase
          .from('program_likes')
          .select('id')
          .eq('program_id', programId)
          .eq('user_id', currentUserId)
          .maybeSingle(),
        supabase
          .from('program_saves')
          .select('id')
          .eq('program_id', programId)
          .eq('user_id', currentUserId)
          .maybeSingle(),
      ])

      isLiked = !!likeResult.data
      isSaved = !!saveResult.data
    }

    return {
      ...data,
      author: author ? {
        id: author.id,
        full_name: author.full_name || 'Community Coach',
        avatar_url: author.avatar_url,
        specialty: author.specialty,
      } : {
        id: data.user_id,
        full_name: 'Community Coach',
        avatar_url: null,
        specialty: null,
      },
      items: programItems,
      item_count: programItems.length,
      total_duration: programItems.reduce(
        (sum, item) => sum + (item.video?.duration_seconds || 0),
        0
      ),
      is_liked: isLiked,
      is_saved: isSaved,
    }
  },

  /**
   * Like a program
   */
  async likeProgram(programId: string, userId: string): Promise<boolean> {
    const { error } = await supabase.from('program_likes').insert({
      program_id: programId,
      user_id: userId,
    } as ProgramLikeInsert)

    if (error) {
      // Ignore duplicate likes (already liked)
      if (error.code === '23505') return true
      console.error('Failed to like program:', error)
      throw error
    }

    return true
  },

  /**
   * Unlike a program
   */
  async unlikeProgram(programId: string, userId: string): Promise<boolean> {
    const { error } = await supabase
      .from('program_likes')
      .delete()
      .eq('program_id', programId)
      .eq('user_id', userId)

    if (error) {
      console.error('Failed to unlike program:', error)
      throw error
    }

    return true
  },

  /**
   * Toggle like status
   */
  async toggleLike(
    programId: string,
    userId: string,
    currentlyLiked: boolean
  ): Promise<boolean> {
    if (currentlyLiked) {
      await this.unlikeProgram(programId, userId)
      return false
    } else {
      await this.likeProgram(programId, userId)
      return true
    }
  },

  /**
   * Save a program to user's collection
   */
  async saveProgram(programId: string, userId: string): Promise<boolean> {
    const { error } = await supabase.from('program_saves').insert({
      program_id: programId,
      user_id: userId,
    } as ProgramSaveInsert)

    if (error) {
      // Ignore duplicate saves (already saved)
      if (error.code === '23505') return true
      console.error('Failed to save program:', error)
      throw error
    }

    return true
  },

  /**
   * Unsave a program from user's collection
   */
  async unsaveProgram(programId: string, userId: string): Promise<boolean> {
    const { error } = await supabase
      .from('program_saves')
      .delete()
      .eq('program_id', programId)
      .eq('user_id', userId)

    if (error) {
      console.error('Failed to unsave program:', error)
      throw error
    }

    return true
  },

  /**
   * Toggle save status
   */
  async toggleSave(
    programId: string,
    userId: string,
    currentlySaved: boolean
  ): Promise<boolean> {
    if (currentlySaved) {
      await this.unsaveProgram(programId, userId)
      return false
    } else {
      await this.saveProgram(programId, userId)
      return true
    }
  },

  /**
   * Get user's saved programs
   */
  async getSavedPrograms(userId: string): Promise<PublicProgramWithAuthor[]> {
    // Get saved program IDs
    const { data: saves, error: savesError } = await supabase
      .from('program_saves')
      .select('program_id')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })

    if (savesError) {
      console.error('Failed to fetch saved programs:', savesError)
      throw savesError
    }

    if (!saves?.length) {
      return []
    }

    const programIds = saves.map((s) => s.program_id)

    // Fetch the programs
    const { data: programs, error: programsError } = await supabase
      .from('programs')
      .select('*')
      .in('id', programIds)

    if (programsError) {
      console.error('Failed to fetch saved programs:', programsError)
      throw programsError
    }

    if (!programs?.length) {
      return []
    }

    // Get unique author IDs and fetch user profiles
    const authorIds = [...new Set(programs.map((p) => p.user_id))]
    const { data: authors } = await supabase
      .from('users')
      .select('id, full_name, avatar_url, specialty')
      .in('id', authorIds)

    const authorMap = new Map<string, PublicProgramAuthor>()
    authors?.forEach((a) => {
      authorMap.set(a.id, {
        id: a.id,
        full_name: a.full_name || 'Community Coach',
        avatar_url: a.avatar_url,
        specialty: a.specialty,
      })
    })

    // Get item counts
    const { data: itemCounts } = await supabase
      .from('program_items')
      .select('program_id')
      .in('program_id', programIds)

    const countMap = new Map<string, number>()
    itemCounts?.forEach((item) => {
      countMap.set(item.program_id, (countMap.get(item.program_id) || 0) + 1)
    })

    // Maintain the order from saves
    const programMap = new Map(programs.map((p) => [p.id, p]))

    return programIds
      .map((id) => programMap.get(id))
      .filter((p): p is NonNullable<typeof p> => !!p)
      .map((p) => ({
        ...p,
        author: authorMap.get(p.user_id) || {
          id: p.user_id,
          full_name: 'Community Coach',
          avatar_url: null,
          specialty: null,
        },
        item_count: countMap.get(p.id) || 0,
        is_saved: true,
      }))
  },

  /**
   * Get user's liked programs
   */
  async getLikedPrograms(userId: string): Promise<PublicProgramWithAuthor[]> {
    // Get liked program IDs
    const { data: likes, error: likesError } = await supabase
      .from('program_likes')
      .select('program_id')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })

    if (likesError) {
      console.error('Failed to fetch liked programs:', likesError)
      throw likesError
    }

    if (!likes?.length) {
      return []
    }

    const programIds = likes.map((l) => l.program_id)

    // Fetch the programs
    const { data: programs, error: programsError } = await supabase
      .from('programs')
      .select('*')
      .in('id', programIds)

    if (programsError) {
      console.error('Failed to fetch liked programs:', programsError)
      throw programsError
    }

    if (!programs?.length) {
      return []
    }

    // Get unique author IDs and fetch user profiles
    const authorIds = [...new Set(programs.map((p) => p.user_id))]
    const { data: authors } = await supabase
      .from('users')
      .select('id, full_name, avatar_url, specialty')
      .in('id', authorIds)

    const authorMap = new Map<string, PublicProgramAuthor>()
    authors?.forEach((a) => {
      authorMap.set(a.id, {
        id: a.id,
        full_name: a.full_name || 'Community Coach',
        avatar_url: a.avatar_url,
        specialty: a.specialty,
      })
    })

    // Get item counts
    const { data: itemCounts } = await supabase
      .from('program_items')
      .select('program_id')
      .in('program_id', programIds)

    const countMap = new Map<string, number>()
    itemCounts?.forEach((item) => {
      countMap.set(item.program_id, (countMap.get(item.program_id) || 0) + 1)
    })

    // Maintain the order from likes
    const programMap = new Map(programs.map((p) => [p.id, p]))

    return programIds
      .map((id) => programMap.get(id))
      .filter((p): p is NonNullable<typeof p> => !!p)
      .map((p) => ({
        ...p,
        author: authorMap.get(p.user_id) || {
          id: p.user_id,
          full_name: 'Community Coach',
          avatar_url: null,
          specialty: null,
        },
        item_count: countMap.get(p.id) || 0,
        is_liked: true,
      }))
  },

  /**
   * Duplicate a public program with attribution tracking
   */
  async duplicatePublicProgram(
    sourceProgramId: string,
    userId: string,
    newTitle?: string
  ): Promise<ProgramWithItems | null> {
    // Get the original program with items
    const original = await this.getProgramById(sourceProgramId)
    if (!original) return null

    // Get the original author ID
    const { data: originalProgram } = await supabase
      .from('programs')
      .select('user_id')
      .eq('id', sourceProgramId)
      .single()

    // Create the new program with attribution
    const newProgram = await this.createProgram({
      user_id: userId,
      title: newTitle || original.title,
      description: original.description,
      visibility: 'private', // Always start as private
      is_template: false,
      duration_weeks: original.duration_weeks,
      difficulty_level: original.difficulty_level,
      category: original.category,
      tags: original.tags,
      // Attribution fields
      duplicated_from: sourceProgramId,
      original_author_id: originalProgram?.user_id || null,
    })

    // Copy all items
    if (original.items.length > 0) {
      const newItems = original.items.map((item, index) => ({
        program_id: newProgram.id,
        video_id: item.video_id,
        position: index,
        sets: item.sets,
        reps: item.reps,
        duration_seconds: item.duration_seconds,
        rest_seconds: item.rest_seconds,
        day_of_week: item.day_of_week,
        notes: item.notes,
      }))

      const { error } = await supabase.from('program_items').insert(newItems)

      if (error) {
        console.error('Failed to copy program items:', error)
        // Clean up the program if items failed
        await this.deleteProgram(newProgram.id)
        throw error
      }
    }

    // Increment the duplicate count on the original program
    // First get current count, then update
    const { data: currentProgram } = await supabase
      .from('programs')
      .select('duplicate_count')
      .eq('id', sourceProgramId)
      .single()

    await supabase
      .from('programs')
      .update({ duplicate_count: (currentProgram?.duplicate_count || 0) + 1 })
      .eq('id', sourceProgramId)

    // Return the complete new program
    return this.getProgramById(newProgram.id)
  },

  /**
   * Get distinct categories from public programs
   */
  async getPublicCategories(): Promise<string[]> {
    const { data, error } = await supabase
      .from('programs')
      .select('category')
      .eq('visibility', 'public')
      .not('category', 'is', null)

    if (error) {
      console.error('Failed to fetch categories:', error)
      return []
    }

    const categories = new Set<string>()
    data?.forEach((p) => {
      if (p.category) categories.add(p.category)
    })

    return Array.from(categories).sort()
  },

  /**
   * Update program visibility (for toggling public/private)
   */
  async setVisibility(
    programId: string,
    visibility: VideoVisibility
  ): Promise<Program | null> {
    const { data, error } = await supabase
      .from('programs')
      .update({ visibility })
      .eq('id', programId)
      .select()
      .single()

    if (error) {
      console.error('Failed to update visibility:', error)
      throw error
    }

    return data
  },

  /**
   * Get programs created by a specific user (public profile view)
   */
  async getPublicProgramsByAuthor(
    authorId: string,
    currentUserId?: string
  ): Promise<PublicProgramWithAuthor[]> {
    // Fetch programs and author info separately
    const [programsResult, authorResult] = await Promise.all([
      supabase
        .from('programs')
        .select('*')
        .eq('user_id', authorId)
        .eq('visibility', 'public')
        .order('like_count', { ascending: false }),
      supabase
        .from('users')
        .select('id, full_name, avatar_url, specialty')
        .eq('id', authorId)
        .single(),
    ])

    if (programsResult.error) {
      console.error('Failed to fetch author programs:', programsResult.error)
      throw programsResult.error
    }

    const data = programsResult.data
    if (!data?.length) {
      return []
    }

    // Build author object
    const author: PublicProgramAuthor = authorResult.data
      ? {
          id: authorResult.data.id,
          full_name: authorResult.data.full_name || 'Community Coach',
          avatar_url: authorResult.data.avatar_url,
          specialty: authorResult.data.specialty,
        }
      : {
          id: authorId,
          full_name: 'Community Coach',
          avatar_url: null,
          specialty: null,
        }

    // Get item counts
    const programIds = data.map((p) => p.id)
    const { data: itemCounts } = await supabase
      .from('program_items')
      .select('program_id')
      .in('program_id', programIds)

    const countMap = new Map<string, number>()
    itemCounts?.forEach((item) => {
      countMap.set(item.program_id, (countMap.get(item.program_id) || 0) + 1)
    })

    // Check likes/saves if user is logged in
    let likedIds = new Set<string>()
    let savedIds = new Set<string>()

    if (currentUserId) {
      const [likesResult, savesResult] = await Promise.all([
        supabase
          .from('program_likes')
          .select('program_id')
          .eq('user_id', currentUserId)
          .in('program_id', programIds),
        supabase
          .from('program_saves')
          .select('program_id')
          .eq('user_id', currentUserId)
          .in('program_id', programIds),
      ])

      if (likesResult.data) {
        likedIds = new Set(likesResult.data.map((l) => l.program_id))
      }
      if (savesResult.data) {
        savedIds = new Set(savesResult.data.map((s) => s.program_id))
      }
    }

    return data.map((p) => ({
      ...p,
      author,
      item_count: countMap.get(p.id) || 0,
      is_liked: likedIds.has(p.id),
      is_saved: savedIds.has(p.id),
    }))
  },
}
